import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero } from '@/components/PageShell';

const AUP_DESCRIPTION =
  "Sport Fund acceptable use policy: what the athlete-fundraising platform may and may not be used for.";

export default function AcceptableUse() {
  usePageMeta('Acceptable Use Policy', AUP_DESCRIPTION);
  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Acceptable use policy"
          title="What the platform is for."
          lead="Sport Fund is for raising money for one clear, honest purpose. This page lists what is not allowed, and what we do when a page breaks the rules."
          size="sm"
        />
      }
    >
      <section className="bg-white">
        <article className="mx-auto max-w-3xl px-6 py-20 md:py-28 prose prose-slate prose-lg prose-headings:font-extrabold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:leading-relaxed prose-a:text-blue-700 prose-a:font-medium hover:prose-a:text-blue-800 prose-strong:text-slate-900">
          <p><em>Sport Fund is operated by BackerBase LLC, a Wyoming limited liability company, 1621 Central Avenue, Cheyenne, WY 82001, USA.</em></p>
          <p><strong>Last updated:</strong> 30 September 2026</p>
          <p>
            This Acceptable Use Policy sets out what Sport Fund may and may not be used for. It applies to everyone who uses Sport Fund: people who create fundraising pages (&ldquo;creators&rdquo;), people who contribute (&ldquo;supporters&rdquo;) and anyone else who visits. It forms part of our <Link to="/terms">Terms of Service</Link>.
          </p>

          <h2>1. What Sport Fund is for</h2>
          <p>
            A Sport Fund page may only raise money for the sporting costs of the athlete named on the page: training, coaching, equipment, competition entry fees, travel and related expenses. Every page must describe that purpose truthfully, and the money raised must be used for it. Contributions are paid to the creator whose page it is, through Stripe; Sport Fund does not hold or pool supporters&rsquo; money.
          </p>

          <h2>2. Prohibited fundraising</h2>
          <p>You may not use Sport Fund to raise money for, promote or pay for any of the following:</p>
          <ul>
            <li>Anything illegal, or anything that breaks the law where you or your supporters are.</li>
            <li>Any business, product or activity on Stripe&rsquo;s <a href="https://stripe.com/legal/restricted-businesses" target="_blank" rel="noopener noreferrer">Prohibited and Restricted Businesses list</a>.</li>
            <li>Medical treatment or medical bills, emergencies, disaster relief, funerals or memorials. Sport Fund does not support these causes.</li>
            <li>Political campaigns, candidates, parties, political action committees, lobbying or ballot measures.</li>
            <li>Investments, loans, securities, equity, profit-sharing or any promise of a financial return.</li>
            <li>Lotteries, raffles, sweepstakes, gambling or games of chance.</li>
            <li>Money that will be passed on to other people or organisations, or any form of money transmission, currency exchange or cryptocurrency.</li>
            <li>Weapons, ammunition, explosives, drugs or drug paraphernalia (including cannabis), tobacco or vaping products.</li>
            <li>Adult or sexually explicit content or services.</li>
            <li>Content or activity that promotes hate, violence, terrorism, harassment, self-harm or discrimination.</li>
            <li>A person, cause or organisation you are not authorised to raise money for, or a purpose you do not intend to use the money for.</li>
          </ul>

          <h2>3. Honesty and identity</h2>
          <ul>
            <li>Your page must be about you, or about someone who has authorised you to raise money for them. You may not impersonate anyone.</li>
            <li>Everything on your page, including stories, photos, goals and updates, must be true and yours to use.</li>
            <li>The person who receives payouts must be at least 18 years old. A page for someone under 18 must be run by their parent or legal guardian.</li>
            <li>Before a page can receive contributions, the creator must complete identity verification and add a bank account with Stripe, our payment processor.</li>
            <li>Before the first payout, we review the page against the identity details Stripe has verified. Payouts start once that review is complete, and we may ask for more information at any time.</li>
            <li>You must use contributions for the purpose described on your page and keep receipts or invoices for your main costs for at least five years. When a page has raised $1,000 since its last report, reaches its goal, or every twelve months, we ask for a short use-of-funds report with those receipts. If it does not reach us within 30 days, contributions to the page are paused until it does.</li>
          </ul>

          <h2>4. Sanctions and restricted places</h2>
          <p>
            You may not use Sport Fund if you are, or act for, a person or organisation named on a sanctions list (including those maintained by the United States, the United Kingdom, the European Union and the United Nations). Sport Fund may not be used to raise money from, send money to or support activity in Cuba, Iran, North Korea, Syria, or the Crimea, Donetsk or Luhansk regions, or any other place where Stripe does not permit transactions. We do not accept contributions or payout sign-ups from internet addresses registered in those countries.
          </p>

          <h2>5. Rules for supporters</h2>
          <ul>
            <li>Only use a payment method you are authorised to use.</li>
            <li>Do not use Sport Fund to launder money, test stolen cards or move money for anyone else.</li>
            <li>If you believe a page breaks this policy, tell us (see section 8) rather than filing a chargeback first. We will look into it.</li>
          </ul>

          <h2>6. Payments</h2>
          <p>
            Payments are processed by Stripe. Creators who receive contributions also agree to the <a href="https://stripe.com/connect-account/legal" target="_blank" rel="noopener noreferrer">Stripe Connected Account Agreement</a>, and Stripe may independently review, restrict or close a payment account under its own rules.
          </p>

          <h2>7. How we enforce this policy</h2>
          <p>We may review any page at any time. If we believe a page or account breaks this policy or our Terms of Service, we may do any of the following, with or without notice:</p>
          <ul>
            <li>ask the creator to change the page or provide more information;</li>
            <li>unpublish the page or turn off contributions to it;</li>
            <li>refuse, cancel or refund contributions, and cancel monthly contributions;</li>
            <li>suspend or close the account;</li>
            <li>report the activity to Stripe, and to law enforcement where the law requires it.</li>
          </ul>

          <h2>8. Reporting a page</h2>
          <p>
            If you think a page breaks this policy, email <a href="mailto:info@thesportfund.com">info@thesportfund.com</a> with the link to the page and what concerns you. We review every report.
          </p>

          <h2>9. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. We will post the new version here and update the date above. Continued use of Sport Fund after a change means you accept it.
          </p>
        </article>
      </section>
    </PageShell>
  );
}
